import { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { useTheme, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { styled } from '@mui/system';

import { useDebounce } from '@libHooks';
import InputBase from '@baseComponents/InputBase';
import { theme } from 'app/Theme';

const propTypes = {
    name: PropTypes.string.isRequired,
    handleSearch: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string
};

const defaultProps = {
    placeholder: 'Search',
    value: ''
};

const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
    color: theme.palette.icon.main,
    fontSize: theme.spacing(3),
    transition: 'color 0.3s ease',

    '&:hover': {
        color: theme.palette.icon.dark,
        cursor: 'pointer'
    }
}));

const FilterInput = ({ name, handleSearch, placeholder, value: propValue }) => {
    const [value, setValue] = useState(propValue);
    const { spacing } = useTheme();
    const debounceValue = useDebounce(value, 1000);
    const isMounted = useRef(false);
    const prevValue = useRef('');

    const handleChange = useCallback((e) => {
        setValue(e.target.value);
    }, []);

    const handleKeyPress = useCallback(
        ({ key }) => {
            if (key === 'Enter' && value !== prevValue.current) {
                prevValue.current = value;
                handleSearch(value);
            }
        },
        [value, handleSearch]
    );

    const handleIconClick = useCallback(
        (e) => {
            if (value !== prevValue.current) {
                prevValue.current = value;
                handleSearch(value);
            }
        },
        [value, handleSearch]
    );

    useEffect(() => {
        if (isMounted.current && debounceValue !== prevValue.current) {
            prevValue.current = debounceValue;
            handleSearch(debounceValue);
        } else {
            isMounted.current = true;
        }
    }, [debounceValue, handleSearch]);

    useEffect(() => {
        setValue(propValue);
    }, [propValue]);

    return (
        <InputBase
            name={name}
            input={{ value, onChange: handleChange }}
            placeholder={placeholder}
            styles={{ control: { height: spacing(4.5) } }}
            onKeyPress={handleKeyPress}
            sx={{ backgroundColor: theme.palette.background.white }}
            endAdornment={
                <InputAdornment position='end'>
                    <StyledSearchIcon onClick={handleIconClick} />
                </InputAdornment>
            }
        />
    );
};

FilterInput.prototypes = propTypes;
FilterInput.defaultProps = defaultProps;

export { FilterInput as default, FilterInput };
